<template>
  <!--통역 진행중-->
  <button
    v-if="script.status === 'CHAT_IN_PROGRESS'"
    type="button"
    :class="isDesktopOrTablet ? 'tb-gray-btn s-btn' : 'primary-btn m-btn'"
    @click="openDownloadModal('REAL_TIME')"
  >
    <span>{{ t('download_realtime_btn') }}</span>
  </button>

  <!--번역되지 않은 상태-->
  <button
    v-else-if="script.status === 'NOT_STARTED'"
    type="button"
    :class="isDesktopOrTablet ? 'tb-gray-btn s-btn' : 'primary-btn m-btn'"
    :disabled="!languageCode"
    @click="requestScriptTranslation"
  >
    <span>{{ t('download_request_btn') }}</span>
  </button>

  <!--번역진행중인 상태-->
  <button
    v-else-if="script.status === 'PROCESSING'"
    :class="isDesktopOrTablet ? 'tb-gray-btn s-btn' : 'tb-gray-btn m-btn'"
  >
    <span
      >{{ t('download_generating') }} ({{
        t('download_generating_time', { minute: script.durationInMinutes || 0 })
      }})</span
    >
    <img class="icon" :src="getAssetImageUrl('common/icons/ing-icon.svg')" alt="" />
  </button>

  <!--번역이 완료된 상태-->
  <template v-else-if="script.status === 'COMPLETED'">
    <span v-if="isDesktopOrTablet" class="clear">
      <span>{{ t('button_voice_register') }}</span>
      <IconCheckNew />
    </span>
    <button
      type="button"
      :class="isDesktopOrTablet ? 'tb-gray-btn s-btn' : 'primary-btn m-btn'"
      @click="openDownloadModal('HISTORY')"
    >
      <span>{{ t('download_btn') }}</span>
    </button>
  </template>

  <teleport to="body">
    <ModalFileSelect
      v-if="downloadModal"
      v-model="downloadModal"
      :download-form="downloadForm"
      @confirm="confirmDownloadModal"
      @close="closeDownloadModal"
    />
  </teleport>
</template>
<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { fetchScriptDownload, fetchScriptDownloadRealTime, updateScriptTranslation } from '@/api/apiService';
import IconCheckNew from '@/components/icons/IconCheckNew.vue';
import ModalFileSelect from '@/components/shared/composite/ModalFileSelect.vue';
import { useDeviceSize } from '@/composables/shared/useDeviceSize';
import { useToastStore } from '@/stores/toast';
import { LanguageCode } from '@/types/common';
import { getAssetImageUrl } from '@/utils/getAssetImageUrl';
import { DownloadType, ScriptDownloadForm } from '@/types/components';
import { RequestScriptDownload, RequestScriptRealTime, ScriptDownloadFormat } from '@/types/request';
import { ChatScript } from '@/types/response';
import { FILE_EXTENSIONS } from '@/utils/constants/common';
import { downloadBlobFile, downloadFile } from '@/utils/downloadFile';

const props = withDefaults(
  defineProps<{
    script: ChatScript;
    languageCode: LanguageCode;
  }>(),
  {
    script: () => ({}) as ChatScript,
    languageCode: 'ko',
  }
);

const emit = defineEmits<{ update: [chatId: string, updatedScript: Partial<ChatScript>] }>();

const toastStore = useToastStore();
const { t } = useI18n();
const { isDesktopOrTablet } = useDeviceSize();

const downloadModal = ref<boolean>(false);
const downloadForm = ref<ScriptDownloadForm>({} as ScriptDownloadForm);

const openDownloadModal = (downloadType: DownloadType) => {
  Object.assign(downloadForm.value, {
    ...downloadForm.value,
    downloadType,
    chatId: props.script.chatId,
    languageCode: props.languageCode,
  });
  downloadModal.value = true;
};

const closeDownloadModal = () => {
  downloadForm.value = {} as ScriptDownloadForm;
};

const confirmDownloadModal = (params: RequestScriptDownload) => {
  const { downloadType } = downloadForm.value;
  const { downloadFormat, languageCode, includeSummary } = params;

  if (downloadType === 'REAL_TIME') {
    requestScriptDownloadRealTime({ downloadFormat });
  }
  if (downloadType === 'HISTORY') {
    requestScriptDownload({
      languageCode,
      downloadFormat,
      includeSummary,
      downloadFilename: getDownloadFileName(downloadFormat),
    });
  }

  closeDownloadModal();
};

/**
 * @summary 스크립트 번역 요청
 * @description COMPLETED
 * */
const requestScriptTranslation = async () => {
  try {
    const res = await updateScriptTranslation(props.script.chatId, { languageCode: props.languageCode });
    if (!res.success) {
      throw new Error();
    }

    emit('update', props.script.chatId, { status: res.data.status });
  } catch {
    toastStore.error(t('toast_request_failed'));
  }
};

const getDownloadFileName = (format: ScriptDownloadFormat): string => {
  const fileExtension = FILE_EXTENSIONS[format] || 'txt';
  return `${props.script.chatName}_${props.script.languageCode}.${fileExtension}`;
};

/**
 * @summary 실시간 스트립트 파일 다운로드
 * @description CHAT_IN_PROGRESS
 * @param {RequestScriptRealTime} params
 * */
const requestScriptDownloadRealTime = async (params: RequestScriptRealTime) => {
  try {
    const res = await fetchScriptDownloadRealTime(props.script.chatId, params);
    if (!res.success) {
      throw new Error();
    }
    downloadBlobFile(res.data, getDownloadFileName(params.downloadFormat));
    toastStore.info(t('toast_download_success'));
  } catch {
    toastStore.error(t('toast_download_failed'));
  }
};

/**
 * @summary 스크립트 파일 다운로드
 * @description COMPLETED
 * @param {RequestScriptDownload} params
 * */
const requestScriptDownload = async (params: RequestScriptDownload) => {
  try {
    const res = await fetchScriptDownload(props.script.chatId, params);
    if (!res.success) {
      throw new Error();
    }

    downloadFile({ url: res.data });
    toastStore.info(t('toast_download_success'));
  } catch {
    toastStore.error(t('toast_download_failed'));
  }
};
</script>
<style scoped lang="scss">
.clear {
  color: var(--primary);

  svg {
    margin-left: $size-xs;
  }
}

.tb-gray-btn {
  background: $color-gray-20;
}

:deep(.modal-content) {
  header {
    display: none;
  }
  main {
    padding: 1.25rem;
  }
}
</style>
